import type { Model as BackboneModel } from "backbone";

import { assets as assetsApi } from "apis-v2/callables";
import type { IAsset, ITeamAsset } from "common/interfaces";

import ApiAdapter from "./apiAdapter";

class AssetsAdapter extends ApiAdapter<IAsset, {}> {
    // Personal assets only receive write-once derived fields (type, ink, font labels) after creation, so only shared assets (orgId) listen for updates
    private isSharedAsset = false;
    private hasFetched = false;

    protected get _getApiNotFoundRetryAttempts() {
        // Not found is an expected outcome when checking if an asset exists before creating it,
        // retrying for freshly created assets is handled by Assets.getAssetById()
        return 0;
    }

    async _createApi(model: IAsset): Promise<IAsset> {
        throw new Error("Not supported, use assetsApi.createAsset instead");
    }

    async _updateApi(
        changesetUpdates: Partial<IAsset>, changesetOriginal: Partial<IAsset>
    ): Promise<Partial<IAsset>> {
        const { modifiedAt, _changeId } = await assetsApi.updateAsset({
            id: this.id,
            changesetOriginal,
            changesetUpdates,
            _changeId: (this.model.attributes as IAsset)._changeId,
        });
        return { modifiedAt, _changeId } as Partial<IAsset>;
    }

    async _getApi(): Promise<IAsset> {
        // Only the initial load may be served from the short lived cache, re-fetches (resolving a
        // conflict, model.reload()) are there to get the fresh state so they bypass it
        const reloadCache = this.hasFetched || !!(this.model as BackboneModel<IAsset> & { isReloading?: boolean }).isReloading;
        const asset = await assetsApi.getAssetCached({ id: this.id }, { reloadCache });
        this.hasFetched = true;

        // The base adapter connects pusher before fetching the model, at which point we don't know
        // whether the asset is a shared asset yet, so subscribing once we do
        if ((asset as ITeamAsset).orgId && !this.isSharedAsset) {
            this.isSharedAsset = true;
            await this._connectPusher();
        }

        return asset;
    }

    _composeModel(baseModel: Partial<IAsset>): IAsset {
        return { ...baseModel, id: this.id } as IAsset;
    }

    _getPusherChannelId(): string | null {
        // Pusher doesn't allow ":" in channel names and team asset ids are "<workspaceId>:<uuid>"
        return this.isSharedAsset ? `private-asset-${this.id.replace(/:/g, "@")}` : null;
    }
}

export default AssetsAdapter;
